import { type CardRef, mergeCardRefs } from "./card-ref.js";

export const PARSER_VERSION = 1;

const APOS = "['’]";

export type ActionKind =
  | "opening_hand"
  | "mulligan"
  | "mulligan_draw"
  | "coin_choice"
  | "coin_toss"
  | "go_first"
  | "draw"
  | "draw_named"
  | "play_pokemon"
  | "play_stadium"
  | "play_card"
  | "attach"
  | "evolve"
  | "attack"
  | "use"
  | "knockout"
  | "prize"
  | "retreat"
  | "promote"
  | "switch"
  | "end_turn"
  | "timeout"
  | "concede"
  | "win"
  | "discard"
  | "discard_named"
  | "discard_from"
  | "shuffle"
  | "shuffle_into"
  | "to_hand"
  | "activated"
  | "condition_damage"
  | "status"
  | "status_end"
  | "put_counters"
  | "move_counters"
  | "move_to_hand"
  | "put_deck"
  | "prevented"
  | "took_damage"
  | "tera"
  | "coin_flip"
  | "choice"
  | "breakdown"
  | "drawn_cards"
  | "revealed"
  | "other";

export type Action = {
  line: number;
  text: string;
  kind: ActionKind;
  /** Player the action is attributed to, when the line names one. */
  actor?: string;
  fields: Record<string, string | number | boolean>;
};

export type SubEntry = Action & { details: string[] };
export type Entry = Action & { subs: SubEntry[] };

export type SegmentKind = "setup" | "turn" | "checkup" | "other";

export type Segment = {
  kind: SegmentKind;
  /** 1-based turn number for `turn` segments. */
  turnNumber?: number;
  player?: string;
  title: string;
  entries: Entry[];
};

export type SideStats = {
  cardsDrawn: number;
  energyAttached: number;
  damageDealt: number;
  knockouts: number;
  prizesTaken: number;
  mulligans: number;
  turnsTaken: number;
  pokemonPlayed: string[];
  cardsPlayed: string[];
  evolutions: string[];
  attacks: string[];
};

export type EndReason = "concede" | "opponent_concede" | "prizes" | "other" | "unknown";
export type MySideSource = "debug" | "concede" | "named_draws" | "handle" | "user";

export type ParsedBattleLog = {
  parserVersion: number;
  players: string[];
  segments: Segment[];
  turnCount: number;
  winner?: string;
  endReason: EndReason;
  /** Set when the log itself reveals which side is the local player. */
  mySideHint?: { side: number; source: MySideSource };
  coinTossWinner?: string;
  firstPlayer?: string;
  statsByPlayer: Record<string, SideStats>;
  unparsedCount: number;
  unparsedLines: string[];
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function emptyStats(): SideStats {
  return {
    cardsDrawn: 0,
    energyAttached: 0,
    damageDealt: 0,
    knockouts: 0,
    prizesTaken: 0,
    mulligans: 0,
    turnsTaken: 0,
    pokemonPlayed: [],
    cardsPlayed: [],
    evolutions: [],
    attacks: [],
  };
}

const TURN_HEADER = new RegExp(`^(?:Turn # (\\d+) - )?(.+?)${APOS}s Turn$`);
const SETUP_HEADER = /^Setup$/;
const CHECKUP_HEADER = /^Pokémon Checkup$/;
const SUB_LINE = /^- (.*)$/;
const DETAIL_LINE = /^\s+[•·]\s*(.*)$/;

/** Discover player names from turn headers first, then setup lines. Order = first appearance. */
export function discoverPlayers(lines: readonly string[]): string[] {
  const players: string[] = [];
  const add = (name: string | undefined): void => {
    const trimmed = name?.trim();
    if (trimmed !== undefined && trimmed.length > 0 && !players.includes(trimmed)) {
      players.push(trimmed);
    }
  };
  const fromHeaders: string[] = [];
  for (const line of lines) {
    const header = TURN_HEADER.exec(line.trim());
    if (header !== null && header[2] !== undefined && !fromHeaders.includes(header[2])) {
      fromHeaders.push(header[2]);
    }
  }
  const setupPattern =
    /^(.+?) (?:chose (?:heads|tails) for the opening coin flip|(?:won|lost) the coin toss|decided to go (?:first|second)|drew \d+ cards? for the opening hand|took a mulligan|played .+ to the (?:Active Spot|Bench))\.$/;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === "Setup") continue;
    if (TURN_HEADER.test(trimmed)) break;
    const match = setupPattern.exec(trimmed);
    if (match !== null) {
      const candidate = match[1];
      if (
        candidate !== undefined &&
        (fromHeaders.length === 0 || fromHeaders.includes(candidate))
      ) {
        add(candidate);
      }
    }
  }
  for (const name of fromHeaders) add(name);
  return players;
}

type Pattern = { kind: ActionKind; re: RegExp; map?: (m: RegExpExecArray) => Action["fields"] };

function buildPatterns(players: readonly string[]): Pattern[] {
  const P = players.length > 0 ? `(${players.map(escapeRegExp).join("|")})` : "([A-Za-z0-9_.-]+)";
  const POS = `${P}${APOS}s`;
  const r = (source: string): RegExp => new RegExp(`^${source}$`);
  const num = (value: string | undefined): number =>
    value === undefined || value === "a" ? 1 : Number.parseInt(value, 10);
  return [
    {
      kind: "opening_hand",
      re: r(`${P} drew (\\d+) cards? for the opening hand\\.`),
      map: (m) => ({ n: num(m[2]) }),
    },
    { kind: "mulligan", re: r(`${P} took a mulligan\\.`) },
    {
      kind: "mulligan_draw",
      re: r(`${P} drew (\\d+) more cards? because ${P} took at least \\d+ mulligans?\\.`),
      map: (m) => ({ n: num(m[2]) }),
    },
    {
      kind: "coin_choice",
      re: r(`${P} chose (heads|tails) for the opening coin flip\\.`),
      map: (m) => ({ choice: m[2] ?? "" }),
    },
    {
      kind: "coin_toss",
      re: r(`${P} (won|lost) the coin toss\\.`),
      map: (m) => ({ won: m[2] === "won" }),
    },
    {
      kind: "go_first",
      re: r(`${P} decided to go (first|second)\\.`),
      map: (m) => ({ first: m[2] === "first" }),
    },
    { kind: "draw", re: r(`${P} drew a card\\.`), map: () => ({ n: 1 }) },
    { kind: "draw", re: r(`${P} drew (\\d+) cards?\\.`), map: (m) => ({ n: num(m[2]) }) },
    {
      kind: "draw",
      re: r(`${P} drew (\\d+) cards? and played them to the Bench\\.`),
      map: (m) => ({ n: num(m[2]), toBench: true }),
    },
    { kind: "draw_named", re: r(`${P} drew (.+)\\.`), map: (m) => ({ card: m[2] ?? "", n: 1 }) },
    {
      kind: "play_pokemon",
      re: r(`${P} played (.+) to the (Active Spot|Bench)\\.`),
      map: (m) => ({ card: m[2] ?? "", to: m[3] ?? "" }),
    },
    {
      kind: "play_stadium",
      re: r(`${P} played (.+) to the Stadium spot\\.`),
      map: (m) => ({ card: m[2] ?? "" }),
    },
    {
      kind: "attach",
      re: r(`${P} attached (.+) to (.+) (in the Active Spot|on the Bench)\\.`),
      map: (m) => ({ card: m[2] ?? "", target: m[3] ?? "", energy: /\bEnergy\b/.test(m[2] ?? "") }),
    },
    {
      kind: "evolve",
      re: r(`${P} evolved (.+) to (.+) (in the Active Spot|on the Bench)\\.`),
      map: (m) => ({ from: m[2] ?? "", to: m[3] ?? "" }),
    },
    {
      kind: "attack",
      re: r(
        `${POS} (.+?) used (.+?) on ${POS} (.+?) for (\\d+) damage\\.(?: ${POS} .+? took (\\d+) more damage because of (.+?) Weakness\\.)?`,
      ),
      map: (m) => ({
        pokemon: m[2] ?? "",
        move: m[3] ?? "",
        targetOwner: m[4] ?? "",
        target: m[5] ?? "",
        damage: num(m[6]),
        weakness: m[9] ?? "",
      }),
    },
    {
      kind: "use",
      re: r(`${POS} (.+?) used (.+?)\\.`),
      map: (m) => ({ pokemon: m[2] ?? "", move: m[3] ?? "" }),
    },
    {
      kind: "knockout",
      re: r(`${POS} (.+) was Knocked Out!`),
      map: (m) => ({ pokemon: m[2] ?? "" }),
    },
    { kind: "prize", re: r(`${P} took (a|\\d+) Prize cards?\\.`), map: (m) => ({ n: num(m[2]) }) },
    {
      kind: "retreat",
      re: r(`${P} retreated (.+) to the Bench\\.`),
      map: (m) => ({ pokemon: m[2] ?? "" }),
    },
    {
      kind: "promote",
      re: r(`${POS} (.+) is now in the Active Spot\\.`),
      map: (m) => ({ pokemon: m[2] ?? "" }),
    },
    {
      kind: "switch",
      re: r(`${POS} (.+) was switched with ${POS} (.+) to become the Active Pok[ée]mon\\.`),
      map: (m) => ({ pokemon: m[2] ?? "", previous: m[4] ?? "" }),
    },
    { kind: "end_turn", re: r(`${P} ended their turn\\.`) },
    { kind: "timeout", re: r(`${P} didn${APOS}t take an action in time\\.`) },
    {
      kind: "concede",
      re: r(`Opponent conceded\\. ${P} wins\\.`),
      map: (m) => ({ who: "opponent", winner: m[1] ?? "" }),
    },
    {
      kind: "concede",
      re: r(`You conceded\\. ${P} wins\\.`),
      map: (m) => ({ who: "you", winner: m[1] ?? "" }),
    },
    {
      kind: "concede",
      re: r(`${P} conceded\\.`),
      map: (m) => ({ who: "named", loser: m[1] ?? "" }),
    },
    {
      kind: "win",
      re: r(`All Prize cards taken\\. ${P} wins\\.`),
      map: (m) => ({ reason: "prizes", winner: m[1] ?? "" }),
    },
    {
      kind: "win",
      re: r(`(?:(.+?)\\. )?${P} wins\\.`),
      map: (m) => ({ reason: m[1] ?? "other", winner: m[2] ?? "" }),
    },
    { kind: "discard", re: r(`${P} discarded (\\d+) cards?\\.`), map: (m) => ({ n: num(m[2]) }) },
    { kind: "discard_named", re: r(`${P} discarded (.+)\\.`), map: (m) => ({ card: m[2] ?? "" }) },
    {
      kind: "discard_from",
      re: r(`(\\d+) cards? (?:were|was) discarded from ${POS} (.+)\\.`),
      map: (m) => ({ n: num(m[1]), owner: m[2] ?? "", pokemon: m[3] ?? "" }),
    },
    {
      kind: "discard_from",
      re: r(`(.+) was discarded from ${POS} (.+)\\.`),
      map: (m) => ({ card: m[1] ?? "", owner: m[2] ?? "", pokemon: m[3] ?? "" }),
    },
    { kind: "shuffle", re: r(`${P} shuffled their deck\\.`) },
    {
      kind: "shuffle_into",
      re: r(`${P} shuffled (a|\\d+) cards? into their deck\\.`),
      map: (m) => ({ n: num(m[2]) }),
    },
    {
      kind: "shuffle_into",
      re: r(`${P} shuffled (.+) into their deck\\.`),
      map: (m) => ({ card: m[2] ?? "", n: 1 }),
    },
    { kind: "shuffle_into", re: r(`${P} shuffled their hand into their deck\\.`) },
    {
      kind: "to_hand",
      re: r(`(.+) was added to ${POS} hand\\.`),
      map: (m) => ({ card: m[1] ?? "", owner: m[2] ?? "" }),
    },
    { kind: "activated", re: r(`(.+) was activated\\.`), map: (m) => ({ card: m[1] ?? "" }) },
    {
      kind: "condition_damage",
      re: r(
        `(a|\\d+) damage counters? (?:were|was) placed on ${POS} (.+) for the Special Condition (.+)\\.`,
      ),
      map: (m) => ({ n: num(m[1]), owner: m[2] ?? "", pokemon: m[3] ?? "", condition: m[4] ?? "" }),
    },
    {
      kind: "status",
      re: r(`${POS} (.+) is now (Poisoned|Burned|Asleep|Paralyzed|Confused)\\.`),
      map: (m) => ({ pokemon: m[2] ?? "", condition: m[3] ?? "" }),
    },
    {
      kind: "status_end",
      re: r(`${POS} (.+) is no longer (Poisoned|Burned|Asleep|Paralyzed|Confused)\\.`),
      map: (m) => ({ pokemon: m[2] ?? "", condition: m[3] ?? "" }),
    },
    {
      kind: "put_counters",
      re: r(`${P} put (a|\\d+) damage counters? on ${POS} (.+)\\.`),
      map: (m) => ({ n: num(m[2]), owner: m[3] ?? "", pokemon: m[4] ?? "" }),
    },
    {
      kind: "move_counters",
      re: r(`${P} moved (a|\\d+) damage counters? from ${POS} (.+) to ${POS} (.+)\\.`),
      map: (m) => ({ n: num(m[2]) }),
    },
    {
      kind: "move_to_hand",
      re: r(`${P} moved ${POS} (.+) to their hand\\.`),
      map: (m) => ({ card: m[3] ?? "" }),
    },
    {
      kind: "put_deck",
      re: r(`${P} put (a|\\d+) cards? on top of their deck\\.`),
      map: (m) => ({ n: num(m[2]) }),
    },
    {
      kind: "prevented",
      re: r(`Damage to (.+) was prevented\\.`),
      map: (m) => ({ pokemon: m[1] ?? "" }),
    },
    {
      kind: "took_damage",
      re: r(`${POS} (.+) took (\\d+) damage\\.`),
      map: (m) => ({ pokemon: m[2] ?? "", damage: num(m[3]) }),
    },
    {
      kind: "tera",
      re: r(`${POS} (.+)${APOS}s Tera effect was used\\.`),
      map: (m) => ({ pokemon: m[2] ?? "" }),
    },
    {
      kind: "coin_flip",
      re: r(
        "(?:\\[playerName\\]|.+?) flipped (?:a coin and it landed on (heads|tails)|.+?coins?, and .+? landed on heads)\\.",
      ),
      map: (m) => ({ result: m[1] ?? "multi" }),
    },
    { kind: "choice", re: r(`${P} chose (.+?)\\.?`), map: (m) => ({ choice: m[2] ?? "" }) },
    { kind: "breakdown", re: r("Damage breakdown:") },
    { kind: "drawn_cards", re: r("(\\d+) drawn cards?\\."), map: (m) => ({ n: num(m[1]) }) },
    {
      kind: "revealed",
      re: r("Cards revealed from Mulligan (\\d+)"),
      map: (m) => ({ mulligan: num(m[1]) }),
    },
  ];
}

const ACTOR_FIRST_KINDS = new Set<ActionKind>([
  "opening_hand",
  "mulligan",
  "mulligan_draw",
  "coin_choice",
  "coin_toss",
  "go_first",
  "draw",
  "draw_named",
  "play_pokemon",
  "play_stadium",
  "play_card",
  "attach",
  "evolve",
  "attack",
  "use",
  "knockout",
  "prize",
  "retreat",
  "promote",
  "switch",
  "end_turn",
  "timeout",
  "discard",
  "discard_named",
  "shuffle",
  "shuffle_into",
  "status",
  "status_end",
  "put_counters",
  "choice",
  "move_counters",
  "move_to_hand",
  "put_deck",
  "took_damage",
  "tera",
]);

function classify(
  text: string,
  patterns: readonly Pattern[],
  playPattern: RegExp,
  line: number,
): Action {
  for (const pattern of patterns) {
    const match = pattern.re.exec(text);
    if (match === null) continue;
    const fields = pattern.map?.(match) ?? {};
    const action: Action = { line, text, kind: pattern.kind, fields };
    if (ACTOR_FIRST_KINDS.has(pattern.kind) && match[1] !== undefined) action.actor = match[1];
    if (pattern.kind === "concede" || pattern.kind === "win") {
      const winner = fields.winner;
      if (typeof winner === "string" && winner.length > 0) action.actor = winner;
    }
    if (
      pattern.kind === "discard_from" ||
      pattern.kind === "to_hand" ||
      pattern.kind === "condition_damage"
    ) {
      const owner = fields.owner;
      if (typeof owner === "string" && owner.length > 0) action.actor = owner;
    }
    return action;
  }
  const play = playPattern.exec(text);
  if (play !== null) {
    return { line, text, kind: "play_card", actor: play[1], fields: { card: play[2] ?? "" } };
  }
  return { line, text, kind: "other", fields: {} };
}

/** Parse the stock battle-log section (everything after `=== Battle Log ===`, or the whole clipboard export). */
export function parseBattleLog(battleLogText: string): ParsedBattleLog {
  const lines = battleLogText.replace(/\r\n?/g, "\n").split("\n");
  const players = discoverPlayers(lines);
  const patterns = buildPatterns(players);
  const P = players.length > 0 ? `(${players.map(escapeRegExp).join("|")})` : "([A-Za-z0-9_.-]+)";
  const playPattern = new RegExp(`^${P} played (.+)\\.$`);

  const segments: Segment[] = [];
  let current: Segment | undefined;
  let lastEntry: Entry | undefined;
  let lastSub: SubEntry | undefined;
  let turnCount = 0;
  const unparsedLines: string[] = [];

  const ensureSegment = (): Segment => {
    if (current === undefined) {
      current = { kind: "other", title: "", entries: [] };
      segments.push(current);
    }
    return current;
  };

  for (let index = 0; index < lines.length; index += 1) {
    const raw = lines[index] ?? "";
    const line = raw.replace(/\s+$/, "");
    if (line.trim().length === 0) continue;
    const lineNo = index + 1;

    const detail = DETAIL_LINE.exec(line);
    if (detail !== null && (lastSub !== undefined || lastEntry !== undefined)) {
      const target = lastSub ?? { details: [] as string[] };
      target.details.push(detail[1] ?? "");
      if (lastSub === undefined && lastEntry !== undefined) {
        // Detail directly under a main entry (no dash line) — attach as a synthetic sub.
        const sub: SubEntry = {
          line: lineNo,
          text: "",
          kind: "other",
          fields: {},
          details: target.details,
        };
        lastEntry.subs.push(sub);
        lastSub = sub;
      }
      continue;
    }

    const trimmed = line.trim();
    if (SETUP_HEADER.test(trimmed)) {
      current = { kind: "setup", title: trimmed, entries: [] };
      segments.push(current);
      lastEntry = undefined;
      lastSub = undefined;
      continue;
    }
    const turnHeader = TURN_HEADER.exec(trimmed);
    if (turnHeader !== null) {
      turnCount += 1;
      current = {
        kind: "turn",
        turnNumber: turnCount,
        player: turnHeader[2],
        title: trimmed,
        entries: [],
      };
      segments.push(current);
      lastEntry = undefined;
      lastSub = undefined;
      continue;
    }
    if (CHECKUP_HEADER.test(trimmed)) {
      current = { kind: "checkup", title: trimmed, entries: [] };
      segments.push(current);
      lastEntry = undefined;
      lastSub = undefined;
      continue;
    }

    const sub = SUB_LINE.exec(line);
    if (sub !== null && lastEntry !== undefined) {
      const action = classify((sub[1] ?? "").trim(), patterns, playPattern, lineNo);
      const subEntry: SubEntry = { ...action, details: [] };
      lastEntry.subs.push(subEntry);
      lastSub = subEntry;
      if (action.kind === "other") unparsedLines.push(line);
      continue;
    }

    const action = classify(trimmed, patterns, playPattern, lineNo);
    const entry: Entry = { ...action, subs: [] };
    ensureSegment().entries.push(entry);
    lastEntry = entry;
    lastSub = undefined;
    if (action.kind === "other") unparsedLines.push(line);
  }

  const result: ParsedBattleLog = {
    parserVersion: PARSER_VERSION,
    players,
    segments,
    turnCount,
    endReason: "unknown",
    statsByPlayer: {},
    unparsedCount: unparsedLines.length,
    unparsedLines,
  };
  for (const player of players) result.statsByPlayer[player] = emptyStats();
  deriveOutcome(result);
  deriveStats(result);
  deriveMySideHint(result);
  return result;
}

function* allActions(
  parsed: ParsedBattleLog,
): Generator<{ action: Action; entry: Entry; segment: Segment; isSub: boolean }> {
  for (const segment of parsed.segments) {
    for (const entry of segment.entries) {
      yield { action: entry, entry, segment, isSub: false };
      for (const sub of entry.subs) yield { action: sub, entry, segment, isSub: true };
    }
  }
}

function otherPlayer(parsed: ParsedBattleLog, name: string): string | undefined {
  return parsed.players.find((player) => player !== name);
}

function deriveOutcome(parsed: ParsedBattleLog): void {
  for (const { action } of allActions(parsed)) {
    if (action.kind === "coin_toss" && action.fields.won === true && action.actor !== undefined) {
      parsed.coinTossWinner = action.actor;
    }
    if (action.kind === "go_first" && action.actor !== undefined) {
      parsed.firstPlayer =
        action.fields.first === true ? action.actor : otherPlayer(parsed, action.actor);
    }
    if (action.kind === "concede") {
      const who = action.fields.who;
      if (who === "named") {
        const loser = String(action.fields.loser ?? "");
        parsed.winner = otherPlayer(parsed, loser);
        parsed.endReason = "concede";
      } else {
        parsed.winner = String(action.fields.winner ?? "");
        parsed.endReason = who === "you" ? "concede" : "opponent_concede";
      }
    }
    if (action.kind === "win") {
      parsed.winner = String(action.fields.winner ?? "");
      parsed.endReason = action.fields.reason === "prizes" ? "prizes" : "other";
    }
  }
}

function pushUnique(list: string[], value: string): void {
  if (!list.includes(value)) list.push(value);
}

function deriveStats(parsed: ParsedBattleLog): void {
  const stats = parsed.statsByPlayer;
  const get = (name: string | undefined): SideStats | undefined =>
    name === undefined ? undefined : stats[name];
  for (const segment of parsed.segments) {
    if (segment.kind === "turn" && segment.player !== undefined) {
      const s = get(segment.player);
      if (s !== undefined) s.turnsTaken += 1;
    }
  }
  for (const { action } of allActions(parsed)) {
    const s = get(action.actor);
    switch (action.kind) {
      case "opening_hand":
      case "draw":
      case "draw_named":
      case "mulligan_draw":
        if (s !== undefined) s.cardsDrawn += Number(action.fields.n ?? 0);
        break;
      case "mulligan":
        if (s !== undefined) s.mulligans += 1;
        break;
      case "attach":
        if (s !== undefined && action.fields.energy === true) s.energyAttached += 1;
        break;
      case "play_pokemon":
        if (s !== undefined) s.pokemonPlayed.push(String(action.fields.card ?? ""));
        break;
      case "play_card":
      case "play_stadium":
        if (s !== undefined) s.cardsPlayed.push(String(action.fields.card ?? ""));
        break;
      case "evolve":
        if (s !== undefined) s.evolutions.push(String(action.fields.to ?? ""));
        break;
      case "attack":
        if (s !== undefined) {
          s.damageDealt += Number(action.fields.damage ?? 0);
          pushUnique(s.attacks, String(action.fields.move ?? ""));
        }
        break;
      case "knockout": {
        const victim = action.actor;
        const attacker = victim === undefined ? undefined : otherPlayer(parsed, victim);
        const a = get(attacker);
        if (a !== undefined) a.knockouts += 1;
        break;
      }
      case "prize":
        if (s !== undefined) s.prizesTaken += Number(action.fields.n ?? 0);
        break;
      default:
        break;
    }
  }
}

function deriveMySideHint(parsed: ParsedBattleLog): void {
  for (const { action } of allActions(parsed)) {
    if (action.kind !== "concede") continue;
    const who = action.fields.who;
    const winner = String(action.fields.winner ?? "");
    if (who === "opponent") {
      const side = parsed.players.indexOf(winner);
      if (side >= 0) parsed.mySideHint = { side, source: "concede" };
      return;
    }
    if (who === "you") {
      const loser = otherPlayer(parsed, winner);
      const side = loser === undefined ? -1 : parsed.players.indexOf(loser);
      if (side >= 0) parsed.mySideHint = { side, source: "concede" };
      return;
    }
  }
  // Only the local player's draws are named in the export.
  const named = new Map<string, number>();
  for (const { action, isSub } of allActions(parsed)) {
    if (isSub || action.kind !== "draw_named" || action.actor === undefined) continue;
    named.set(action.actor, (named.get(action.actor) ?? 0) + 1);
  }
  const withNamed = parsed.players.filter((player) => (named.get(player) ?? 0) > 0);
  if (withNamed.length === 1 && withNamed[0] !== undefined) {
    parsed.mySideHint = { side: parsed.players.indexOf(withNamed[0]), source: "named_draws" };
  }
}

/** Match a configured in-game handle to a player name (prefix / possessive tolerant, case-insensitive). */
export function matchHandle(
  players: readonly string[],
  handles: readonly string[],
): number | undefined {
  for (const handle of handles) {
    const h = handle.trim().toLowerCase();
    if (h.length === 0) continue;
    const index = players.findIndex((player) => player.toLowerCase() === h);
    if (index >= 0) return index;
  }
  return undefined;
}

/** Cards a side made public through its own actions, as name references with approximate counts. */
export function observedCards(parsed: ParsedBattleLog, player: string): CardRef[] {
  const refs: CardRef[] = [];
  const seenByDraw = new Set<string>();
  for (const { action } of allActions(parsed)) {
    if (action.actor !== player) continue;
    switch (action.kind) {
      case "play_pokemon":
      case "play_card":
      case "play_stadium":
      case "discard_named":
        refs.push({ name: String(action.fields.card ?? ""), count: 1 });
        break;
      case "attach":
        refs.push({ name: String(action.fields.card ?? ""), count: 1 });
        break;
      case "evolve":
        refs.push({ name: String(action.fields.to ?? ""), count: 1 });
        break;
      case "draw_named":
        seenByDraw.add(String(action.fields.card ?? ""));
        break;
      case "discard_from": {
        const card = action.fields.card;
        if (typeof card === "string" && card.length > 0) refs.push({ name: card, count: 1 });
        break;
      }
      default:
        break;
    }
  }
  const merged = mergeCardRefs(refs.filter((ref) => (ref.name ?? "").length > 0));
  for (const ref of merged) {
    const isBasicEnergy = /^Basic .+ Energy$/.test(ref.name ?? "");
    if (!isBasicEnergy && ref.count > 4) ref.count = 4;
  }
  const known = new Set(merged.map((ref) => (ref.name ?? "").toLowerCase()));
  for (const name of seenByDraw) {
    if (!known.has(name.toLowerCase())) merged.push({ name, count: 1 });
  }
  return merged.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
}

/** Best-effort archetype label: the two most prominent Pokémon a side showed. */
export function archetypeFromStats(stats: SideStats | undefined): string | undefined {
  if (stats === undefined) return undefined;
  const score = new Map<string, number>();
  for (const name of stats.pokemonPlayed) score.set(name, (score.get(name) ?? 0) + 1);
  for (const name of stats.evolutions) score.set(name, (score.get(name) ?? 0) + 2);
  for (const [name, value] of score) {
    if (/\bex$/.test(name) || /^Mega /.test(name)) score.set(name, value + 2);
  }
  const top = [...score.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([name]) => name);
  return top.length > 0 ? top.join(" / ") : undefined;
}
